import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiFilter, FiEdit, FiLayers, FiZap, FiSearch, FiX } from 'react-icons/fi';

import Layout from '../components/Layout';
import theme from '../styles/theme';
import { planosAPI } from '../services/api';
import { VeiculosPaginacao } from '../components/veiculos';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${theme.spacing[4]};
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.md};
  color: ${theme.colors.text.muted};
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const PrimaryButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${theme.colors.primary[500]}40;
  }

  &:active {
    transform: translateY(0);
  }
`;

const FilterButton = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  background: transparent;
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const FiltersCard = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const FiltersHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-weight: 600;
  color: ${theme.colors.text.secondary};
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${theme.spacing[4]};
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  color: ${theme.colors.text.secondary};
`;

const Input = styled.input`
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const FiltersFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
`;

const ContentCard = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  color: ${({ $ativo }) => ($ativo ? theme.colors.success[600] : theme.colors.error[600])};
  background: ${({ $ativo }) => ($ativo ? theme.colors.success[100] : theme.colors.error[100])};
`;

const GhostButton = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: none;
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.primary[500]}15;
    color: ${theme.colors.primary[600]};
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;
  gap: ${theme.spacing[4]};
`;

const EmptyIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.text.muted};
`;

const EmptyTitle = styled.h3`
  margin: 0;
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
`;

const EmptyText = styled.p`
  margin: 0;
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  max-width: 420px;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${theme.spacing[4]};
`;

const PlanoCard = styled.div`
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
  box-shadow: ${theme.shadows.sm};
`;

const PlanoHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${theme.spacing[3]};
`;

const PlanoTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const PlanoNome = styled.h3`
  margin: 0;
  font-size: ${theme.fontSizes.lg};
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const PlanoMeta = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: ${theme.spacing[3]};
`;

const PlanoMetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const PlanoMetaLabel = styled.span`
  font-size: ${theme.fontSizes.xs};
  font-weight: 600;
  color: ${theme.colors.text.muted};
  text-transform: uppercase;
`;

const PlanoMetaValue = styled.span`
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  color: ${theme.colors.text.primary};
`;

const PlanoDescription = styled.p`
  margin: 0;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.muted};
  line-height: 1.5;
  min-height: 72px;
  display: flex;
  align-items: flex-start;

  @media (max-width: ${theme.breakpoints.md}) {
    min-height: 0;
  }
`;

const PlanoActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
  margin-top: auto;
`;

const PlanoActionButton = styled.button`
  flex: 1;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid
    ${({ $primary }) => ($primary ? theme.colors.primary[500] : theme.colors.border.primary)};
  background: ${({ $primary }) => ($primary ? theme.colors.primary[500] : 'transparent')};
  color: ${({ $primary }) =>
    $primary ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  min-height: 44px;

  &:hover {
    background: ${({ $primary }) =>
      $primary ? theme.colors.primary[600] : theme.colors.background.primary};
    color: ${({ $primary }) =>
      $primary ? theme.colors.text.primary : theme.colors.primary[500]};
    border-color: ${({ $primary }) =>
      $primary ? theme.colors.primary[600] : theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PlanoActionIcon = styled.div`
  font-size: ${theme.fontSizes.base};
`;

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value || 0);

const formatNumber = (value) =>
  new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 0,
  }).format(value || 0);

const PLANOS_STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'true', label: 'Ativos' },
  { value: 'false', label: 'Inativos' },
];

const Planos = () => {
  const navigate = useNavigate();
  const [planos, setPlanos] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [filtros, setFiltros] = useState({
    search: '',
    ativo: '',
    page: 1,
    limit: 10,
  });
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  const carregarPlanos = async (params = filtros) => {
    try {
      setLoading(true);
      setErro(null);
      const response = await planosAPI.listar(params);
      if (response.data?.success) {
        const lista = response.data.data || [];
        const ordenada = [...lista].sort((a, b) => Number(a.valor || 0) - Number(b.valor || 0));
        setPlanos(ordenada);
        setPagination(response.data.pagination || pagination);
      } else {
        setErro('Não foi possível carregar os planos.');
      }
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
      setErro('Erro ao carregar planos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarPlanos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFiltroChange = (campo, valor) => {
    setFiltros((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const handleAplicarFiltros = () => {
    const params = { ...filtros, page: 1 };
    setFiltros(params);
    carregarPlanos(params);
  };

  const handleLimparFiltros = () => {
    const params = {
      search: '',
      ativo: '',
      page: 1,
      limit: 10,
    };
    setFiltros(params);
    carregarPlanos(params);
  };

  const handlePageChange = (nextPage) => {
    const params = { ...filtros, page: nextPage };
    setFiltros(params);
    carregarPlanos(params);
  };

  const handleItemsPerPageChange = (newLimit) => {
    const params = { ...filtros, limit: newLimit, page: 1 };
    setFiltros(params);
    carregarPlanos(params);
  };

  const listaVazia = !loading && planos.length === 0;

  const resumo = useMemo(() => {
    const totalPlanos = pagination.totalItems || 0;
    const ativos = planos.filter((plano) => plano.ativo).length;
    return {
      totalPlanos,
      ativos,
      inativos: totalPlanos - ativos,
    };
  }, [planos, pagination.totalItems]);

  return (
    <Layout title="Planos Comerciais">
      <Container>
        <Header>
          <TitleGroup>
            <Title>Catálogo de Planos</Title>
            <Subtitle>
              Gerencie os planos comerciais, seus créditos mensais e recursos disponíveis.{' '}
              {resumo.totalPlanos > 0 && (
                <>Total: {resumo.totalPlanos} planos ({resumo.ativos} ativos).</>
              )}
            </Subtitle>
          </TitleGroup>
          <Actions>
            <PrimaryButton onClick={() => navigate('/gestao/planos/novo')}>
              <FiPlus />
              Novo Plano
            </PrimaryButton>
          </Actions>
        </Header>

        <FiltersCard>
          <FiltersHeader>
            <FiFilter />
            Filtros de busca
          </FiltersHeader>
          <FiltersGrid>
            <FilterGroup>
              <Label>Buscar por nome</Label>
              <Input
                placeholder="Ex.: Enterprise"
                value={filtros.search}
                onChange={(e) => handleFiltroChange('search', e.target.value)}
              />
            </FilterGroup>
            <FilterGroup>
              <Label>Status</Label>
              <Select
                value={filtros.ativo}
                onChange={(e) => handleFiltroChange('ativo', e.target.value)}
              >
                {PLANOS_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FilterGroup>
          </FiltersGrid>
          <FiltersFooter>
            <FilterButton onClick={handleLimparFiltros}>
              <FiX />
              Limpar Filtros
            </FilterButton>
            <FilterButton onClick={handleAplicarFiltros} disabled={loading}>
              <FiSearch />
              {loading ? 'Buscando...' : 'Buscar'}
            </FilterButton>
          </FiltersFooter>
        </FiltersCard>

        <ContentCard>
          {erro && (
            <EmptyState>
              <EmptyIcon>⚠️</EmptyIcon>
              <EmptyTitle>Não foi possível carregar os planos</EmptyTitle>
              <EmptyText>{erro}</EmptyText>
              <PrimaryButton onClick={() => carregarPlanos()}>
                Tentar novamente
              </PrimaryButton>
            </EmptyState>
          )}

          {loading && !erro && (
            <EmptyState>
              <EmptyIcon>
                <FiLayers />
              </EmptyIcon>
              <EmptyTitle>Carregando planos comerciais</EmptyTitle>
              <EmptyText>Aguarde enquanto buscamos as informações mais recentes.</EmptyText>
            </EmptyState>
          )}

          {listaVazia && !erro && (
            <EmptyState>
              <EmptyIcon>
                <FiLayers />
              </EmptyIcon>
              <EmptyTitle>Nenhum plano encontrado</EmptyTitle>
              <EmptyText>
                Ajuste os filtros de busca ou cadastre um novo plano comercial para começar.
              </EmptyText>
              <PrimaryButton onClick={() => navigate('/gestao/planos/novo')}>
                <FiPlus />
                Cadastrar plano
              </PrimaryButton>
            </EmptyState>
          )}

          {!loading && !erro && planos.length > 0 && (
            <>
              <CardsGrid>
                {planos.map((plano) => (
                  <PlanoCard key={plano.id}>
                    <PlanoHeader>
                      <PlanoTitle>
                        <PlanoNome>{plano.nome}</PlanoNome>
                        {plano.descricao && <PlanoDescription>{plano.descricao}</PlanoDescription>}
                      </PlanoTitle>
                      <StatusBadge $ativo={plano.ativo}>
                        <FiZap />
                        {plano.ativo ? 'Ativo' : 'Inativo'}
                      </StatusBadge>
                    </PlanoHeader>

                    <PlanoMeta>
                      <PlanoMetaItem>
                        <PlanoMetaLabel>Créditos mensais</PlanoMetaLabel>
                        <PlanoMetaValue>{formatNumber(plano.creditos_mensais)} créditos</PlanoMetaValue>
                      </PlanoMetaItem>
                      <PlanoMetaItem>
                        <PlanoMetaLabel>Valor mensal</PlanoMetaLabel>
                        <PlanoMetaValue>{formatCurrency(plano.valor)}</PlanoMetaValue>
                      </PlanoMetaItem>
                    </PlanoMeta>

                    <PlanoActions>
                      <PlanoActionButton
                        $primary
                        onClick={() => navigate(`/gestao/planos/${plano.id}/editar`)}
                      >
                        <PlanoActionIcon>
                          <FiEdit />
                        </PlanoActionIcon>
                        Editar
                      </PlanoActionButton>
                    </PlanoActions>
                  </PlanoCard>
                ))}
              </CardsGrid>

              <VeiculosPaginacao
                pagination={pagination}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                loading={loading}
              />
            </>
          )}
        </ContentCard>
      </Container>
    </Layout>
  );
};

export default Planos;


